import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import SlimHeader from './SlimHeader';
import FooterNav from './FooterNav';
import LandingPage from './LandingPage';
import { DomainData } from '../types';
import yaml from 'js-yaml';
import './Home.css';

// Lazy load overlay components for code splitting
const Terminal = lazy(() => import('./Terminal'));
const SystemArchitectureOverlay = lazy(() => import('./SystemArchitectureOverlay'));
const ShowcaseGalleryOverlay = lazy(() => import('./ShowcaseGalleryOverlay'));

// Loading spinner component
const OverlayLoader: React.FC = () => (
  <div className="overlay-loader">
    <div className="loader-spinner" />
    <span className="loader-text">LOADING...</span>
  </div>
);

type ActiveOverlay = 'none' | 'ecosystem' | 'terminal' | 'gallery';
type GeometryMode = 'GRID' | 'PALACE' | 'CATHEDRAL';

const Home: React.FC = () => {
  const [treeData, setTreeData] = useState<DomainData | null>(null);
  const [activeOverlay, setActiveOverlay] = useState<ActiveOverlay>(() => {
    // Check URL parameters on initial load
    const params = new URLSearchParams(window.location.search);
    if (params.get('terminal') === 'true') return 'terminal';
    if (params.get('ecosystem') === 'true') return 'ecosystem';
    if (params.get('gallery') === 'true') return 'gallery';
    return 'none';
  });
  const [vibeMode, setVibeMode] = useState(() => {
    return localStorage.getItem('vibeMode') === 'true';
  });
  
  // Showcase 4D controls (lifted from TopologyPalace)
  const [isFunky, setIsFunky] = useState(false);
  const [isVisualizer, setIsVisualizer] = useState(false);
  const [geometryMode, setGeometryMode] = useState<GeometryMode>('PALACE');

  // Persist vibeMode to localStorage
  useEffect(() => {
    localStorage.setItem('vibeMode', String(vibeMode));
  }, [vibeMode]);

  // Handle URL parameter changes (for deep linking)
  useEffect(() => {
    const handleURLChange = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('terminal') === 'true') setActiveOverlay('terminal');
      else if (params.get('ecosystem') === 'true') setActiveOverlay('ecosystem');
      else if (params.get('gallery') === 'true') setActiveOverlay('gallery');
    };

    window.addEventListener('popstate', handleURLChange);
    return () => window.removeEventListener('popstate', handleURLChange);
  }, []);

  // Load domain data
  useEffect(() => {
    fetch('/myDomains.yaml')
      .then(response => response.text())
      .then(data => {
        const parsedData = yaml.load(data) as DomainData;
        setTreeData(parsedData);
      })
      .catch(error => console.error('Error loading YAML data:', error));
  }, []);

  // Close overlay handler
  const closeOverlay = useCallback(() => {
    setActiveOverlay('none');
  }, []);

  // Toggle VIBE MODE
  const handleVibeToggle = useCallback(() => {
    setVibeMode(prev => !prev);
  }, []);

  // Open terminal overlay (from showcase gallery)
  const handleOpenTerminal = useCallback(() => {
    setActiveOverlay('terminal');
  }, []);

  // Showcase control handlers
  const handleColorize = useCallback(() => {
    setIsFunky(prev => !prev);
    if ('vibrate' in navigator) navigator.vibrate(10);
  }, []);

  const handleVisualize = useCallback(() => {
    setIsVisualizer(prev => !prev);
    if ('vibrate' in navigator) navigator.vibrate(10);
  }, []);

  const handleTopologize = useCallback(() => {
    setGeometryMode(prev => prev === 'GRID' ? 'PALACE' : prev === 'PALACE' ? 'CATHEDRAL' : 'GRID');
    if ('vibrate' in navigator) navigator.vibrate(10);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K for vibe mode toggle
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setVibeMode(prev => !prev);
        return;
      }

      // ESC to close overlay
      if (e.key === 'Escape' && activeOverlay !== 'none') {
        closeOverlay();
      }

      // Arrow keys for quick navigation (only when no overlay active)
      if (activeOverlay === 'none') {
        if (e.key === 'ArrowLeft') setActiveOverlay('ecosystem');
        else if (e.key === 'ArrowDown') setActiveOverlay('gallery');
        else if (e.key === 'ArrowRight') setActiveOverlay('terminal');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeOverlay, closeOverlay]);

  const isOverlayActive = activeOverlay !== 'none';

  return (
    <div className={`home-wrapper ${vibeMode ? 'vibe-mode' : ''} ${isOverlayActive ? 'overlay-active' : ''}`}>
      {/* Slim Header - Always visible */}
      <SlimHeader activeOverlay={activeOverlay} />

      {/* Landing Page - Topology Constellation (hidden when overlay active) */}
      {!isOverlayActive && (
        <LandingPage setActiveOverlay={setActiveOverlay} />
      )}

      {/* Overlay Content Area */}
      {isOverlayActive && (
        <div className="overlay-content-area" role="main" aria-label={`${activeOverlay} view`}>
          <Suspense fallback={<OverlayLoader />}>
            {activeOverlay === 'ecosystem' && (
              <SystemArchitectureOverlay
                treeData={treeData}
                onClose={closeOverlay}
                vibeMode={vibeMode}
              />
            )}

            {activeOverlay === 'gallery' && (
              <ShowcaseGalleryOverlay
                onClose={closeOverlay}
                vibeMode={vibeMode}
                onOpenTerminal={handleOpenTerminal}
                isFunky={isFunky}
                isVisualizer={isVisualizer}
                geometryMode={geometryMode}
              />
            )}

            {activeOverlay === 'terminal' && (
              <Terminal
                onClose={closeOverlay}
                onVibeToggle={handleVibeToggle}
                vibeMode={vibeMode}
                initialCommand="cv"
              />
            )}
          </Suspense>
        </div>
      )}

      {/* Footer Navigation - Always visible */}
      <FooterNav
        activeOverlay={activeOverlay}
        setActiveOverlay={setActiveOverlay}
        vibeMode={vibeMode}
        onVibeToggle={handleVibeToggle}
        // Showcase controls (only active when gallery overlay is open)
        showcaseControls={activeOverlay === 'gallery' ? {
          isFunky,
          isVisualizer,
          geometryMode,
          onColorize: handleColorize,
          onVisualize: handleVisualize,
          onTopologize: handleTopologize,
        } : undefined}
      />
    </div>
  );
};

export default Home;
